import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  catchError,
  finalize,
  map,
  Observable,
  share,
  switchMap,
  throwError,
} from 'rxjs';

import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { MessageService } from 'primeng/api';
import { environment } from '../../../environments/environment';
import { isPublicAuthRoute } from '../../auth/shared/public-auth-routes';
import { HomeState } from '../../modules/home/core/state/home.state';
import { AuthHorusResponse } from '../services/authentication/authentication.model';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { AuthEndpoints } from '../services/endpoints/core.endpoints';
import { MeProfile } from '../services/profile/me-profile.model';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private platformRefresh$: Observable<string> | null = null;
  private horusToken$: Observable<AuthHorusResponse> | null = null;
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  me = this.store.selectSignal<MeProfile>(HomeState.getMe);
  constructor(private authService: AuthenticationService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const isPlatformApi = req.url.startsWith(environment.baseUrl);
    const isHorusApi = req.url.startsWith(environment.horusBaseUrl);
    const isLprV2 = req.url.startsWith(environment.lprBaseUrlV2);
    const isLprDashboardApi = req.url.startsWith(environment.dashBoardApiUrl);
    const isPublicConfig = req.url.includes(
      'whitelabels/public/configurations',
    );

    if (!isPlatformApi && !isHorusApi && !isLprV2 && !isLprDashboardApi) {
      return next.handle(req);
    }

    if (isPublicConfig) {
      return next.handle(req);
    }

    if (isPlatformApi && this.isLoginFlowRequest(req.url)) {
      return next.handle(req);
    }

    if (req.url.endsWith(`/${AuthEndpoints.refresh}`)) {
      return next.handle(req);
    }

    if (isPlatformApi || isLprV2 || isLprDashboardApi) {
      const authReq = this.addPlatformAuth(req);

      return next.handle(authReq).pipe(
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return this.handle401Error(authReq, next);
          }
          return throwError(() => error);
        }),
      );
    }

    if (this.authService.tokenHorus()) {
      return this.sendHorusRequest(req, next);
    }

    return this.shareHorusToken(() => this.authService.fetchHorusToken()).pipe(
      switchMap(() => this.sendHorusRequest(req, next)),
    );
  }

  private isLoginFlowRequest(url: string): boolean {
    const path = url.slice(environment.baseUrl.length).split('?')[0];
    const loginPath = `/${AuthEndpoints.login}`;
    return path === loginPath || path.startsWith(`${loginPath}/`);
  }

  private addPlatformAuth(req: HttpRequest<any>): HttpRequest<any> {
    const accessToken = this.authService.localStorageToken();
    if (accessToken) {
      return req.clone({
        headers: req.headers.set('Authorization', `Bearer ${accessToken}`),
      });
    }
    return req;
  }

  private addHorusAuth(req: HttpRequest<any>): HttpRequest<any> {
    const accessTokenHorus =
      this.authService.tokenHorus() ?? localStorage.getItem('tokenHorus');

    if (accessTokenHorus) {
      // The daily dashboard has its own platform filter (whitelabel_id).
      if (req.url === `${environment.horusBaseUrl}/api/v1/recorders/daily/`) {
        return req.clone({
          headers: req.headers.set(
            'Authorization',
            `Bearer ${accessTokenHorus}`,
          ),
        });
      }
      let modifiedBody = req.body;
      let modifiedParams = req.params;
      const me = this.me();

      if (req.method === 'GET') {
        if (me?.whitelabel?.id && !modifiedParams.has('client_id')) {
          modifiedParams = modifiedParams.set(
            'client_id',
            me.whitelabel.id.toString(),
          );
        }

        if (me?.id && !modifiedParams.has('user_id')) {
          modifiedParams = modifiedParams.set('user_id', me.id);
        }
      }

      if (req.method === 'GET' && me?.id && !modifiedParams.has('user_id')) {
        modifiedParams = modifiedParams.set('user_id', String(me.id));
      }

      if (
        req.body &&
        typeof req.body === 'object' &&
        !Array.isArray(req.body)
      ) {
        if (typeof FormData !== 'undefined' && req.body instanceof FormData) {
          const fd = req.body as FormData;
          const newFd = new FormData();
          fd.forEach((value, key) => newFd.append(key, value as any));
          if (me?.id) newFd.append('user_id', String(me.id));
          if (me?.name) newFd.append('user_name', me.name);
          if (me?.whitelabel?.id)
            newFd.append('client_id', me.whitelabel.id.toString());
          modifiedBody = newFd;
        } else {
          const isBinary =
            (typeof File !== 'undefined' && req.body instanceof File) ||
            (typeof Blob !== 'undefined' && req.body instanceof Blob) ||
            (typeof ArrayBuffer !== 'undefined' &&
              req.body instanceof ArrayBuffer);

          if (!isBinary) {
            if (me?.id || me?.name) {
              modifiedBody = {
                ...(req.body || {}),
                ...(me?.id ? { user_id: me.id } : {}),
                ...(me?.name ? { user_name: me.name } : {}),
                ...(me?.whitelabel?.id
                  ? { client_id: me.whitelabel.id.toString() }
                  : {}),
              };
            } else {
              modifiedBody = { ...(req.body || {}) };
            }
          } else {
            modifiedBody = req.body;
          }
        }
      }

      return req.clone({
        headers: req.headers.set('Authorization', `Bearer ${accessTokenHorus}`),
        params: modifiedParams,
        body: modifiedBody,
      });
    }
    return req;
  }

  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return this.refreshPlatformToken().pipe(
      switchMap((token) =>
        next
          .handle(
            req.clone({
              headers: req.headers.set('Authorization', `Bearer ${token}`),
            }),
          )
          .pipe(
            catchError((error) => {
              if (error instanceof HttpErrorResponse && error.status === 401) {
                this.endPlatformSession();
              }
              return throwError(() => error);
            }),
          ),
      ),
    );
  }

  private refreshPlatformToken(): Observable<string> {
    if (!this.platformRefresh$) {
      this.platformRefresh$ = this.authService.refreshAccessToken().pipe(
        map(({ refreshToken }) => refreshToken.token),
        catchError((error) => {
          this.endPlatformSession();
          return throwError(() => error);
        }),
        finalize(() => (this.platformRefresh$ = null)),
        share({ resetOnRefCountZero: false }),
      );
    }

    return this.platformRefresh$;
  }

  private endPlatformSession(): void {
    const currentUrl = this.router.url;

    if (isPublicAuthRoute(currentUrl, window.location.pathname)) {
      return;
    }

    this.authService.signOut('expired');

    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

    if (
      !currentUrl.includes('/reset-password') &&
      !currentUrl.includes('/confirm-email') &&
      !returnUrl?.includes('/mosaic-view')
    ) {
      this.router.navigate(['/login'], {
        queryParamsHandling: 'preserve',
      });
    }
  }

  private sendHorusRequest(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const sentToken = this.authService.tokenHorus();
    const authReq = this.addHorusAuth(req);

    return next.handle(authReq).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401HorusError(authReq, next, sentToken);
        }
        return throwError(() => error);
      }),
    );
  }

  private handle401HorusError(
    req: HttpRequest<any>,
    next: HttpHandler,
    rejectedToken: string,
  ): Observable<HttpEvent<any>> {
    return this.shareHorusToken(() =>
      this.authService.renewHorusToken(rejectedToken),
    ).pipe(
      switchMap(({ access }) =>
        next.handle(
          req.clone({
            headers: req.headers.set('Authorization', `Bearer ${access}`),
          }),
        ),
      ),
    );
  }

  private shareHorusToken(
    load: () => Observable<AuthHorusResponse>,
  ): Observable<AuthHorusResponse> {
    if (!this.horusToken$) {
      this.horusToken$ = load().pipe(
        catchError((error) => {
          if (!(error instanceof HttpErrorResponse && error.status === 401)) {
            this.notifyHorusUnavailable();
          }
          return throwError(() => error);
        }),
        finalize(() => (this.horusToken$ = null)),
        share({ resetOnRefCountZero: false }),
      );
    }

    return this.horusToken$;
  }

  private notifyHorusUnavailable(): void {
    this.messageService.add({
      key: 'horus-unavailable',
      severity: 'error',
      summary: this.translate.instant('horus_unavailable_title'),
      detail: this.translate.instant('horus_unavailable_detail'),
    });
  }
}
